<template>
  <div class="content">
    <Header @onBack="onBack" class="header_box" :robotInfo="robotInfo"></Header>
    <div class="content_chat">
      <ChatBox
        ref="chatBox"
        class="chatBox"
        :chatData="chatData"
        :isTyping="isTyping"
        :robotInfo="robotInfo"
        @sendBtn="sendBtn"
        @sendVoice="sendVoiceMsg"
      />

      <!-- 流式开关 -->
      <div class="stream-toggle" @click="useStream = !useStream">
        <span class="stream-toggle__dot" :class="{ active: useStream }"></span>
        <span class="stream-toggle__label">流式{{ useStream ? '开' : '关' }}</span>
      </div>

      <transition name="fade">
        <div v-if="loading" class="loading-mask">
          <div class="loading-spinner">
            <span class="dot"></span>
            <span class="dot"></span>
            <span class="dot"></span>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>
<script>
import Header from "@/components/headerView/index.vue";
import ChatBox from "@/components/chatBox";
import {
  createConversation,
  createConversationStream,
  selectConversationList
} from "../services/chatAI/index";
import { selectApi as selectRobot } from "@/services/robot/index";
import {
  addApi as addConversationGroup,
  selectApi as selectConversationGroup
} from "@/services/conversationGroup/index";
export default {
  name: "DataCustomerList",
  components: {
    Header,
    ChatBox
  },
  async created() {
    //取路由上的参数 /:bot/:uuid?
    const params = this.$route.params;
    const firstText = this.$route.query?.firstText;
    this.firstText = firstText; // 保存 firstText 到 data 中
    // console.log("params:", params, "firstText:", firstText);
    // 显示加载特效
    this.loading = true;
    try {
      //根据botCode获取bot信息，若不存在跳转到产品介绍页
      let botRes = await selectRobot({
        robot_code: params?.botCode
      });
      if (botRes.code === 200 && botRes.data.length > 0) {
        this.robotInfo = botRes.data[0];
        this.useStream = this.robotInfo.is_stream === 1;
      } else {
        this.loading = false;
        this.$router.replace(`/pricing`);
        return;
      }

      //1.根据路由uuid初始化会话 或 根据本地uuid初始化会话（兼容微信环境）
      let uuid = this.getUuid(
        this.robotInfo.robot_code,
        this.robotInfo.organization_code
      );
      if (uuid) {
        let selectConversationGroupRes = await selectConversationGroup({
          uuid: uuid,
          robot_code: this.robotInfo.robot_code
        });
        if (
          selectConversationGroupRes.code === 200 &&
          selectConversationGroupRes.data.length === 1
        ) {
          this.conversation_group_uuid = uuid;
          // 路由跳转时保留 firstText 参数
          const query = this.firstText ? { firstText: this.firstText } : {};
          const targetPath = `/conversation/${this.robotInfo.robot_code}/${uuid}`;

          // 避免重复导航错误
          if (
            this.$route.path !== targetPath ||
            JSON.stringify(this.$route.query) !== JSON.stringify(query)
          ) {
            this.$router
              .replace({
                path: targetPath,
                query
              })
              .catch(err => {
                // 忽略 NavigationDuplicated 错误
                if (err.name !== "NavigationDuplicated") {
                  console.error(err);
                }
              });
          }

          await this.handleSelect();
          // 如果是已存在的会话且有 firstText，也自动发送
          if (this.firstText) {
            this.autoSendFirstText();
          }
          this.loading = false;
          return;
        }
      }
      //2.创建会话
      await this.createConversation();
      //3.如果存在 firstText，自动发送给机器人
      if (this.firstText) {
        this.autoSendFirstText();
      }
    } catch (e) {
      console.error(e);
    } finally {
      this.loading = false;
    }
  },
  data() {
    return {
      robotInfo: {},
      query: {},
      conversation_group_uuid: "",
      loadingDom: "",
      loading: false,
      isTyping: false, // 机器人输入中状态
      chatData: [], //回话列表数据
      firstText: "", // 保存首次自动发送的文本
      useStream: false // 是否启用流式回复
    };
  },
  computed: {},

  watch: {},

  methods: {
    /**
     * 获取uuid - 兼容微信环境
     * 优先级：路由参数 > Cookie > localStorage
     * @param {string} robotCode - 机器人代码
     * @param {string} orgCode - 组织代码
     */
    getUuid(robotCode, orgCode) {
      // 1. 优先从路由参数获取
      let uuid = this.$route.params?.uuid;

      // 2. 其次从 Cookie 获取（微信环境兼容性更好）
      if (!uuid && robotCode) {
        const cookieKey = `conversation_uuid_${robotCode}`;
        const cookieValue = document.cookie
          .split("; ")
          .find(row => row.startsWith(`${cookieKey}=`));
        if (cookieValue) {
          uuid = cookieValue.split("=")[1];
        }
      }

      // 3. 最后尝试从 localStorage 获取
      if (!uuid && robotCode) {
        try {
          const storageKey = `uuid_${robotCode}`;
          uuid = localStorage.getItem(storageKey) || "";
        } catch (e) {
          console.warn("localStorage 不可用:", e);
          uuid = "";
        }
      }

      return uuid || "";
    },

    /**
     * 保存uuid - 兼容微信环境
     * 同时保存到 Cookie 和 localStorage
     * @param {string} uuid - 会话UUID
     * @param {string} robotCode - 机器人代码
     * @param {string} orgCode - 组织代码
     */
    setUuid(uuid, robotCode, orgCode) {
      if (!uuid || !robotCode || !orgCode) return;

      // 1. 保存到 Cookie（微信环境兼容性更好，有效期1年）
      const maxAge = 365 * 24 * 60 * 60; // 1年
      const cookieKey = `conversation_uuid_${robotCode}`;
      document.cookie = `${cookieKey}=${uuid}; path=/; max-age=${maxAge}`;

      // 2. 尝试保存到 localStorage
      try {
        const storageKey = `uuid_${robotCode}`;
        localStorage.setItem(storageKey, uuid);
      } catch (e) {
        console.warn("localStorage 保存失败:", e);
      }
    },

    /**
     * 初始化会话
     */
    async createConversation() {
      try {
        // this.loadingText = "正在初始化对话";
        // 调用创建会话接口
        const params = {
          org: this.robotInfo.organization_code,
          bot: this.robotInfo.robot_code
          // 如果有其他参数也可以加上,比如首句作为自动回复
        };
        const response = await addConversationGroup(params);
        console.log("创建会话响应:", response);
        if (response.code === 200 && response.data.uuid) {
          // 会话uuid存入本地（兼容微信环境）
          this.conversation_group_uuid = response.data.uuid;
          this.setUuid(
            response.data.uuid,
            this.robotInfo.robot_code,
            this.robotInfo.organization_code
          );
          // 路由跳转时保留 firstText 参数
          const query = this.firstText ? { firstText: this.firstText } : {};
          const targetPath = `/conversation/${this.robotInfo.robot_code}/${response.data.uuid}`;

          // 避免重复导航错误
          if (
            this.$route.path !== targetPath ||
            JSON.stringify(this.$route.query) !== JSON.stringify(query)
          ) {
            this.$router
              .replace({
                path: targetPath,
                query
              })
              .catch(err => {
                // 忽略 NavigationDuplicated 错误
                if (err.name !== "NavigationDuplicated") {
                  console.error(err);
                }
              });
          }
        }
      } catch (error) {
        console.error("创建会话失败:", error);
      }
    },

    /**
     * 查询会话记录，不存在则 兜底创建会话
     */
    async handleSelect() {
      let res = await selectConversationList({
        conversation_group_uuid: this.conversation_group_uuid,
        robot_code: this.robotInfo.robot_code,
        pageNumber: 1,
        pageSize: 100
      });
      console.log("查询会话记录：", res);
      if (res.code === 200) {
        this.chatData = res.data || [];
        this.bottomScrollClick_chatBox();
      }
    },

    /* 清空会话 */
    clearChat() {
      this.chatData = [];
    },

    /* 清空会话且导航滚动回到顶部 */
    clearChatGoTop() {
      this.chatData = [];
      this.topScrollClick_chatClass();
    },

    /* 创建会话按钮 */
    createChatBoxBtn() {
      this.clearChatGoTop();
      this.$message.success("当前新会话窗口已开启,请输入内容,将自动创建");
    },

    /* 自动发送 firstText */
    async autoSendFirstText() {
      console.log(
        "自动发送 firstText:",
        this.firstText,
        this.conversation_group_uuid,
        "chatData长度:",
        this.chatData.length
      );
      if (!this.firstText || !this.conversation_group_uuid) return;

      // 只在没有会话内容时发送
      if (this.chatData && this.chatData.length > 0) {
        console.log("会话已有内容，跳过自动发送");
        this.firstText = ""; // 清空避免后续重复发送
        return;
      }

      // 稍微延迟，确保界面已准备好
      await this.$nextTick();
      setTimeout(() => {
        this.sendBtn(this.firstText);
        // 发送后清空 firstText，避免重复发送
        this.firstText = "";
        // 清理路由参数中的 firstText
        this.$router
          .replace({
            path: this.$route.path,
            query: {}
          })
          .catch(err => {
            if (err.name !== "NavigationDuplicated") {
              console.error(err);
            }
          });
      }, 300);
    },

    /* 发送按钮 */
    async sendBtn(text) {
      // console.log("发送按钮==========", text, this.chatData);
      if (this.$refs.chatBox) {
        this.$refs.chatBox.textarea = "";
      }

      const createTime = new Date()
        .toLocaleString("zh-CN", { hour12: false })
        .replace(/\//g, "-");

      this.chatData.push({
        content: text,
        role: "user",
        createTime,
        conversation_group_uuid: this.conversation_group_uuid
      });
      this.bottomScrollClick_chatBox();

      // 1 秒内未收到第一个字才显示 typing 动画
      const typingTimer = setTimeout(() => {
        this.isTyping = true;
        this.bottomScrollClick_chatBox();
      }, 1000);

      if (this.useStream) {
        // ---- 流式模式 ----
        // 收到第一个 chunk 时才插入助手气泡
        let assistantIndex = -1;

        createConversationStream(
          {
            message: text,
            msgType: 1,
            conversation_group_uuid: this.conversation_group_uuid
          },
          // onChunk：逐字追加
          delta => {
            clearTimeout(typingTimer);
            this.isTyping = false;
            if (assistantIndex === -1) {
              // 首次收到内容时插入气泡
              assistantIndex = this.chatData.length;
              this.chatData.push({
                content: delta,
                msgType: 1,
                role: "assistant",
                createTime: new Date().toISOString(),
                conversation_group_uuid: this.conversation_group_uuid
              });
            } else {
              const item = this.chatData[assistantIndex];
              this.$set(this.chatData, assistantIndex, {
                ...item,
                content: item.content + delta
              });
            }
            this.bottomScrollClick_chatBox();
          },
          // onDone
          () => {
            clearTimeout(typingTimer);
            this.isTyping = false;
            this.bottomScrollClick_chatBox();
          },
          // onError
          err => {
            clearTimeout(typingTimer);
            this.isTyping = false;
            console.error("流式回复失败:", err);
          }
        );
      } else {
        // ---- 非流式模式 ----
        const result = await createConversation({
          message: text,
          msgType: 1,
          conversation_group_uuid: this.conversation_group_uuid
        });
        clearTimeout(typingTimer);
        this.isTyping = false;
        if (result.code === 200 && result.data) {
          let content = result.data.content;
          // if (result.data.msgType === 3) {
          //   try {
          //     content = JSON.parse(content);
          //   } catch (e) {
          //     /* ignore */
          //   }
          // }
          this.chatData.push({
            content,
            msgType: result.data.msgType,
            role: "assistant",
            createTime: result.data.createTime || new Date().toISOString(),
            conversation_group_uuid: this.conversation_group_uuid
          });
          this.bottomScrollClick_chatBox();
        }
      }
    },

    /* 发送语音消息 */
    async sendVoiceMsg({ url, voiceTime }) {
      if (!url || !this.conversation_group_uuid) return;

      const createTime = new Date()
        .toLocaleString("zh-CN", { hour12: false })
        .replace(/\//g, "-");

      // 1. 立即推入本地聊天列表（乐观更新）
      const tempId = Date.now();
      this.chatData.push({
        id: tempId,
        content: url,
        msgType: 2,
        voiceTime: String(voiceTime),
        role: "user",
        createTime,
        conversation_group_uuid: this.conversation_group_uuid
      });
      this.bottomScrollClick_chatBox();

      // 3. 调用 createConversation API（机器人处理）
      // 1 秒内未收到响应才显示 typing 动画
      const typingTimer = setTimeout(() => {
        this.isTyping = true;
        this.bottomScrollClick_chatBox();
      }, 1000);

      const result = await createConversation({
        message: url,
        msgType: 2,
        voiceTime: String(voiceTime),
        conversation_group_uuid: this.conversation_group_uuid
      });

      clearTimeout(typingTimer);
      this.isTyping = false;

      if (result.code === 200) {
        if (!result.data) {
          return;
        }
        let content = null;
        this.chatData.push({
          content,
          msgType: result.data.msgType,
          role: "assistant",
          createTime: result.data.createTime || new Date().toISOString(),
          conversation_group_uuid: this.conversation_group_uuid
        });
      }
      this.bottomScrollClick_chatBox();
    },

    onBack() {
      this.$router.go(-1);
    },

    /* 相关工具方法 */
    bottomScrollClick_chatBox() {
      this.$nextTick(() => {
        let scrollEl = this.$refs.chatBox.$refs.chat_dom;
        scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: "smooth" });
      });
    },
    topScrollClick_chatBox() {
      this.$nextTick(() => {
        let scrollEl = this.$refs.chatBox.$refs.chat_dom;
        scrollEl.scrollTo({ top: 0, behavior: "smooth" });
      });
    },
    topScrollClick_chatClass() {
      this.$nextTick(() => {
        let scrollEl = this.$refs.chatClass.$refs.chatClass_ul;
        scrollEl.scrollTo({ top: 0, behavior: "smooth" });
      });
    },

    /* 加载特效函数 */
    createLoading() {
      return this.$loading({
        lock: true, // 是否禁止背景滚动，默认为false
        text: "加载中", // 加载文本提示，默认为'Loading'
        spinner: "el-icon-loading", // 加载图标类型，默认为'el-icon-loading'
        background: "rgba(0, 0, 0, 0.2)", // 遮罩层背景颜色，默认为'rgba(0, 0, 0, 0.7)'
        target: document.querySelector(".body")
      });
    },
    closeLoading(loadingDom) {
      loadingDom.close();
    }
  }
};
</script>

<style scoped>
.content {
  background: #edf3ff;
  width: 100%;
  max-width: 800px;
  height: 100%;
}
/* 内容区域 */
.content_chat {
  /* border: 5px solid red; */
  height: calc(100vh - 70px);
  background: #edf3ff;
  margin: 0 auto;
  /* padding: 0 30px; */
  position: relative;
}

/* .chatClass {
  max-width: 380px;
  min-width: 200px;
  width: 20%;
} */
.chatBox {
  margin: 0 auto;
  width: 100%;
  max-width: 800px;
  /* margin-left: 10px; */
}
/* 流式开关 */
.stream-toggle {
  position: absolute;
  top: 10px;
  right: 12px;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 5px;
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid #d0d7e8;
  border-radius: 20px;
  padding: 4px 10px 4px 6px;
  cursor: pointer;
  user-select: none;
  font-size: 12px;
  color: #555;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
}
.stream-toggle__dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ccc;
  transition: background 0.2s;
  flex-shrink: 0;
}
.stream-toggle__dot.active {
  background: #409eff;
}
.stream-toggle__label {
  white-space: nowrap;
}

.loading-mask {
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}
.loading-spinner {
  display: flex;
  gap: 8px;
}
.loading-spinner .dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #409eff;
  animation: loading-bounce 1s infinite alternate;
}
.loading-spinner .dot:nth-child(2) {
  animation-delay: 0.2s;
}
.loading-spinner .dot:nth-child(3) {
  animation-delay: 0.4s;
}
@keyframes loading-bounce {
  0% {
    transform: translateY(0);
    opacity: 0.7;
  }
  100% {
    transform: translateY(-18px);
    opacity: 1;
  }
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
